import { AfterViewInit, Component, inject, Input } from '@angular/core';
import { mapJsonToForm } from './ai-document.mapper';
import urlJoin from 'url-join';
import { environment } from '@environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClientType } from '@modules/universal-billing/renters/renters.enum';
import {
  ContractRateType,
  ContractRentType,
  ContractStatus,
  DepositPayment,
  DepositType
} from '@modules/universal-billing/contract/contract.enums';
import { AutocompleteComponent } from "@shared/components/autocomplete/autocomplete.component";
import { InputValidatorDirective } from "@common/directive/input-validator.directive";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "@shared/services/api.service";
import { InputMaskModule } from "@primeng/inputmask";
import {
  InputValidatorComponent
} from "@common/components/input-validator-component/input-validator-component/input-validator";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import {
  NgLabelTemplateDirective,
  NgOptionComponent,
  NgOptionTemplateDirective,
  NgSelectComponent
} from "@ng-select/ng-select";
import { CheckboxModule } from "@primeng/checkbox";
import { DatepickerComponent } from "@shared/components/datepicker/datepicker.component";
import { InputLabelComponent } from "@shared/components/input-label/input-label.component";
import { InputNumberModule } from "@primeng/inputnumber";
import { ApartmentsService } from "@modules/universal-billing/apartments/apartments.service";
import { PropertiesService } from "@modules/settings/properties/properties.service";
import { CustomNumberPipe } from "@common/pipe/custom-number.pipe";
import { RadioButtonModule } from "@primeng/radiobutton";
import rateTypeList from "@common/rent-type-groups";
import { ButtonModule } from '@primeng/button';
import { BasicForm } from '@shared/components/classes/basic.form';
import { ContractService } from '@modules/universal-billing/contract/contract.service';
import { getResourceById, Resources } from '@app/resources';
import { Router } from '@angular/router';

@Component({
  selector: 'app-ai-document-result',
  standalone: true,
  imports: [AutocompleteComponent, FormsModule, InputValidatorDirective, ReactiveFormsModule, InputMaskModule, InputValidatorComponent, TranslatePipe, NgSelectComponent, NgOptionComponent, CheckboxModule, DatepickerComponent, InputLabelComponent, InputNumberModule, NgLabelTemplateDirective, NgOptionTemplateDirective, CustomNumberPipe, RadioButtonModule, ButtonModule],
  templateUrl: './ai-document-result.component.html',
})
export class AiDocumentResultComponent extends BasicForm<any> implements AfterViewInit {
  @Input() response: any;
  _defaultService = inject(ContractService);
  pdfUrl: SafeResourceUrl;
  router = inject(Router);
  total = 0;
  fields = [];
  override dateFields = ['startDate', 'terminationExtensionDate', 'endDate', 'validFrom', 'depositDueDate'];
  depositTypeList = [
    DepositType.NO_DEPOSIT,
    DepositType.CASH_DEPOSIT,
    DepositType.BANK_TRANSFER,
    DepositType.PLEDGED_SAVINGS_ACCOUNT,
    DepositType.BANK_GUARANTEE,
  ];
  protected api = inject(ApiService);
  protected translateService = inject(TranslateService);
  form: FormGroup = new FormGroup({
    renter: new FormGroup({
      renterType: new FormControl(),
      salutationId: new FormControl(),
      code: new FormControl(),
      firstName: new FormControl(),
      lastName: new FormControl(),
      companyName: new FormControl(),
      phoneNumber: new FormControl(),
      email: new FormControl(),
      bankIban: new FormControl(),
      bankAccountHolder: new FormControl(),
      bankBic: new FormControl(),
      bankName: new FormControl(),
      street: new FormControl(),
      houseNumber: new FormControl(),
      postalCode: new FormControl(),
      city: new FormControl(),
      country: new FormControl(this.translateService.instant('APARTMENT.GERMANY')),
    }),
    rentalDetails: new FormGroup({
      rentType: new FormControl(null),
      startDate: new FormControl(null),
      endDate: new FormControl(),
      permanent: new FormControl(true),
      residentsQuantity: new FormControl(null, [Validators.max(99)]),
      apartmentId: new FormControl(null),
      buildingId: new FormControl(null),
    }),
    payment: new FormGroup({
      unitType: new FormControl(ContractRateType.FlatRateRent),
      flatRate: new FormControl(),
      coldRent: new FormControl(),
      totalPrepayment: new FormControl(),
      operatingCost: new FormControl(),
      heatingCost: new FormControl(),
      validFrom: new FormControl(),
    }),
    sideCosts: new FormArray([]),
    deposit: new FormGroup({
      depositType: new FormControl(DepositType.NO_DEPOSIT),
      depositAmount: new FormControl(),
      depositDueDate: new FormControl(),
      depositPayment: new FormControl(DepositPayment.NOT_SELECTED),
    }),
    notes: new FormControl(''),
    status: new FormControl(ContractStatus.ACTIVE),
    billingPeriod: new FormControl(),
  });
  protected sanitizer = inject(DomSanitizer);
  protected apartmentsService = inject(ApartmentsService);
  protected propertyService = inject(PropertiesService);
  protected readonly ClientType = ClientType;
  protected readonly ContractRentType = ContractRentType;
  protected readonly rateTypeList = rateTypeList;
  protected readonly ContractRateType = ContractRateType;
  protected readonly DepositPayment = DepositPayment;

  ngAfterViewInit() {
    if (this.response) {
      mapJsonToForm(this.response, this.form);
      const url = this.response.data.filePath;
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlJoin(environment.storageUrl, url));
    }

    if (!this.form.get('renter.code')?.value) {
      this.generateCode();
    }

    const renterTypeControl = this.form.get('renter.renterType');
    this.updateCompanyNameValidator(renterTypeControl?.value);
    renterTypeControl?.valueChanges.subscribe(type => this.updateCompanyNameValidator(type));

    const depositTypeControl = this.form.get('deposit.depositType');
    this.updateDepositPayment(depositTypeControl?.value);
    depositTypeControl?.valueChanges.subscribe(type => this.updateDepositPayment(type));
  }

  generateCode() {
    this.api.get('/renters/generate-code').subscribe((res: any) => {
      this.form.get('renter.code')?.setValue(res.data?.nextCode);
    });
  }

  searchBank() {
    const iban = this.form.get('renter.bankIban')?.value;
    if (!iban) {
      this.form.get('renter').patchValue({bankBic: null, bankName: null}, {emitEvent: false});
      return;
    }
    this.api.get('v1/banks', {iban}).subscribe((res: any) => {
      const bank = res.data?.data?.[0];
      this.form.get('renter').patchValue({
        bankBic: bank?.bic ?? null,
        bankName: bank?.name ?? null,
      }, {emitEvent: false});
    });
  }

  searchCity() {
    const postalCode = this.form.get('renter.postalCode')?.value;
    if (!postalCode) {
      return;
    }
    this.api.get('/address-books', {postalCode}).subscribe((res: any) => {
      const address = res.data?.data?.[0];
      if (address) {
        this.form.get('renter.city')?.setValue(address.city);
      }
    });
  }

  submitAction(data: object): Observable<any> {
    return this.api.post('ai-documents/create-contract', data);
  }

  afterSuccessSubmit(res: any) {
    this.router.navigateByUrl(getResourceById(Resources.PropertyContractsDetails, res?.data?.id?.toString()));
  }

  getSalutation(params: any): Observable<any> {
    return this.api.get('/salutation-types', params).pipe(map(res => res.data));
  }

  override convertToRequestData(data: any): any {
    const rentalDetails = data.rentalDetails || {};
    const payment = data.payment || {};
    const deposit = data.deposit || {};
    const renter = data.renter || {};

    const request = {
      renterId: rentalDetails.renterId ?? null,
      apartmentId: rentalDetails.apartmentId ?? null,
      buildingId: rentalDetails.buildingId ?? null,
      startDate: rentalDetails.startDate ?? null,
      terminationExtensionDate: rentalDetails.terminationExtensionDate ?? null,
      endDate: rentalDetails.endDate ?? null,
      permanent: rentalDetails.permanent ?? null,
      residentsQuantity: rentalDetails.residentsQuantity ?? null,
      rentType: rentalDetails.rentType ?? null,
      unitType: payment.unitType ?? null,
      flatRate: payment.flatRate ?? null,
      coldRent: payment.coldRent ?? null,
      totalPrepayment: payment.totalPrepayment ?? 0,
      operatingCost: payment.operatingCost ?? null,
      heatingCost: payment.heatingCost ?? null,
      validFrom: payment.validFrom ?? null,
      depositType: deposit.depositType ?? null,
      depositAmount: deposit.depositAmount ?? null,
      depositDueDate: deposit.depositDueDate ?? null,
      depositPayment: deposit.depositPayment ?? null,
      notes: data.notes ?? null,
      contractStatus: data.status ?? null,
      status: ContractStatus.ACTIVE,
      sideCosts: data.sideCosts ?? [],
      renter: {
        code: renter.code ?? null,
        companyName: renter.companyName ?? null,
        clientType: renter.renterType ?? null,
        salutationId: renter.salutationId ?? null,
        firstName: renter.firstName ?? null,
        lastName: renter.lastName ?? null,
        middleName: renter.middleName ?? null,
        vatId: renter.vatId ?? null,
        bankAccountHolder: renter.bankAccountHolder ?? null,
        bankIban: renter.bankIban ?? null,
        bankBic: renter.bankBic ?? null,
        bankAccountConfirmation: false,
        street: renter.street ?? null,
        country: renter.country ?? null,
        houseNumber: renter.houseNumber ?? null,
        postalCode: renter.postalCode ?? null,
        city: renter.city ?? null,
        email: renter.email ?? null,
        phoneNumber: renter.phoneNumber ?? null,
        alternativePhoneNumber: renter.alternativePhoneNumber ?? null,
        parentId: renter.parentId ?? null,
        status: renter.status ?? null,
        userName: renter.userName ?? null,
        password: renter.password ?? null,
      },
    };

    return super.convertToRequestData(request);
  }

  private updateCompanyNameValidator(type: ClientType) {
    const companyControl = this.form.get('renter.companyName');
    if (type && type !== ClientType.PRIVATE) {
      companyControl?.setValidators([Validators.required]);
    } else {
      companyControl?.clearValidators();
    }
    companyControl?.updateValueAndValidity();
  }

  private updateDepositPayment(type: DepositType) {
    const paymentControl = this.form.get('deposit.depositPayment');
    if (type === DepositType.NO_DEPOSIT) {
      paymentControl?.setValue(DepositPayment.NOT_SELECTED);
      paymentControl?.disable();
    } else {
      paymentControl?.enable();
      if (paymentControl?.value === DepositPayment.NOT_SELECTED) {
        paymentControl.setValue(DepositPayment.ONE_TIME_PAYMENT);
      }
    }
  }
}
